import { useCallback, useEffect } from "react";
import FormHelperText from "@material-ui/core/FormHelperText";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import CircularProgress from "@material-ui/core/CircularProgress";
import Switch from "@material-ui/core/Switch";
import Button from "@material-ui/core/Button";
import Box from "@material-ui/core/Box";
import TextField from "@material-ui/core/TextField";
import Grid from "@material-ui/core/Grid";
import { Controller, useForm } from "react-hook-form";
import { Domain } from "@material-ui/icons";
import { zodResolver } from "@hookform/resolvers/zod";
import { useHistory, useParams } from "react-router-dom";
import { useModalStyles } from "./styles";
import { useAttendanceLocalDepartmentDetails } from "../../../../../../../../../services/api-tansquery/attendance-local/department/details";
import { useBreadcrumbs } from "../../../../../../../../../contexts/breadcrumb";
import ContentForm from "../../../../../../../../../Components/ContentForm";
import { updateDeparmentSchema, UpdateDepartmentSchemaData } from "./schema";
import { useAttendanceLocalDepartmentUpdate } from "../../../../../../../../../services/api-tansquery/attendance-local/department/update";

export default function EditDepartment() {
    const history = useHistory();
    const { classes } = useModalStyles();
    const { setBreadcrumbs } = useBreadcrumbs();
    const { slug, id } = useParams<{ slug: string; id: string }>();
    const { data: department } = useAttendanceLocalDepartmentDetails(Number(id));
    const mutation = useAttendanceLocalDepartmentUpdate(Number(id));
    const { control, handleSubmit, reset } = useForm<UpdateDepartmentSchemaData>({
        resolver: zodResolver(updateDeparmentSchema),
        defaultValues: {
            ativo: department?.ativo,
            sigla: department?.setor?.sigla ?? "",
            nome: department?.setor?.nome ?? "",
        },
    });

    const returnRedirect = useCallback(() => {
        history.replace(`/locais-de-atendimento/${slug}`)
    }, [history, slug]);

    async function handleUpdate(value: UpdateDepartmentSchemaData) {
        const payload: any = {
            ativo: value.ativo,
        };
        
        // Só envia sigla e nome se o setor não for misto e tiver setor associado
        if (department?.setor && !department?.misto) {
            payload.setor_sigla = value.sigla;
            payload.setor_nome = value.nome;
        }
        
        await mutation.mutateAsync(payload);
    };

    useEffect(() => {
        setBreadcrumbs((prev) => {
            const homeBreadcrumb = prev.find((_, index) => index === 0);
            return [
                homeBreadcrumb,
                {
                    label: "Locais de Atendimento",
                    path: "/locais-de-atendimento",
                    icon: Domain,
                },
                {
                    label: "Informações",
                    path: "/locais-de-atendimento",
                },
                {
                    label: "Editar",
                    path: "",
                },
            ];
        });
    }, [setBreadcrumbs]);

    useEffect(() => {
        if (department) {
            reset({
                ativo: department.ativo,
                sigla: department.setor?.sigla ?? "",
                nome: department.setor?.nome ?? "",
            });
        }
    }, [department, reset]);

    useEffect(() => {
        if (mutation.isSuccess) {
            returnRedirect();
        }
    }, [mutation.isSuccess, returnRedirect]);

    const canEditSetorFields = department?.setor && !department?.misto;

    return (
        <ContentForm label="Gerenciar Local de Atendimento - Setor">
            <form onSubmit={handleSubmit(handleUpdate)}>
                <Grid container spacing={2}>
                    {canEditSetorFields && (
                        <>
                            <Grid item xs={12} sm={3}>
                                <Controller
                                    name="sigla"
                                    control={control}
                                    render={({ field, fieldState }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            margin="normal"
                                            variant="outlined"
                                            label="Sigla"
                                            error={!!fieldState.error}
                                            helperText={
                                                fieldState.error
                                                    ? fieldState.error.message
                                                    : "Informe a sigla do setor"
                                            }
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid item xs={12} sm={9}>
                                <Controller
                                    name="nome"
                                    control={control}
                                    render={({ field, fieldState }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            margin="normal"
                                            variant="outlined"
                                            label="Nome"
                                            error={!!fieldState.error}
                                            helperText={
                                                fieldState.error
                                                    ? fieldState.error.message
                                                    : "Informe um nome para o Setor"
                                            }
                                        />
                                    )}
                                />
                            </Grid>
                        </>
                    )}

                    <Grid item xs={12}>
                        <Controller
                            name="ativo"
                            control={control}
                            defaultValue={false}
                            render={({ field }) => (
                                <FormControlLabel
                                    control={<Switch {...field} checked={field.value} />}
                                    label="Este setor está ativo?"
                                />
                            )}
                        />
                        <FormHelperText>
                            Ative esta opção para permitir que este setor seja exibido como disponível.
                        </FormHelperText>
                    </Grid>
                </Grid>

                <Box className={classes.contentActions}>
                    <Button
                        href=""
                        className={classes.cancelButton}
                        onClick={returnRedirect}
                    >
                        Cancelar
                    </Button>
                    <Button
                        className={classes.confirmButton}
                        disabled={mutation.isLoading}
                        variant="contained"
                        type="submit"
                    >
                        {mutation.isLoading ? <CircularProgress /> : "Salvar"}
                    </Button>
                </Box>
            </form>
        </ContentForm>
    );
}
